import React, { useState } from 'react';

const GroupedAnomalies = ({ anomalies, shipment }) => {
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'warning': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getAnomalyIcon = (type) => {
    switch (type) {
      case 'temperature_spike': return '🌡️';
      case 'humidity_drop': return '💧';
      case 'motion_detected': return '🚨';
      case 'gas_leak': return '☁️';
      default: return '⚠️';
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  // Group anomalies by type and severity
  const groupedAnomalies = anomalies.reduce((groups, anomaly) => {
    const key = `${anomaly.type}_${anomaly.severity}`;
    if (!groups[key]) {
      groups[key] = {
        type: anomaly.type,
        severity: anomaly.severity,
        count: 0,
        anomalies: [],
        icon: getAnomalyIcon(anomaly.type),
        color: getSeverityColor(anomaly.severity)
      };
    }
    groups[key].count++;
    groups[key].anomalies.push(anomaly);
    return groups;
  }, {});

  const toggleGroup = (groupKey) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  const getGroupTitle = (type, severity) => {
    const typeName = type.replace('_', ' ').toUpperCase();
    return `${typeName} (${severity})`;
  };

  return (
    <div className="w-80 bg-white border-l border-gray-200 flex flex-col h-full">
      {/* Fixed Header */}
      <div className="flex-shrink-0 p-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Anomalies</h3>
        <p className="text-sm text-gray-600 mt-1">
          {anomalies.length} {anomalies.length === 1 ? 'anomaly' : 'anomalies'} detected
        </p>
      </div>

      {/* Scrollable Anomalies Section */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto">
          {anomalies.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              <div className="text-4xl mb-2">✅</div>
              <p>No anomalies detected</p>
              <p className="text-sm">This shipment completed without issues</p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {Object.entries(groupedAnomalies).map(([groupKey, group]) => (
                <div
                  key={groupKey}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  {/* Group Header */}
                  <div
                    className="p-3 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                    onClick={() => toggleGroup(groupKey)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg">{group.icon}</span>
                        <span className="font-medium text-gray-900">
                          {getGroupTitle(group.type, group.severity)}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${group.color}`}>
                          {group.count}
                        </span>
                        <span className="text-gray-500">
                          {expandedGroups.has(groupKey) ? '▼' : '▶'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Group Details */}
                  {expandedGroups.has(groupKey) && (
                    <div className="p-3 space-y-2 max-h-60 overflow-y-auto">
                      {group.anomalies.slice(0, 5).map((anomaly, index) => (
                        <div
                          key={anomaly.id}
                          className="p-2 border border-gray-100 rounded bg-white"
                        >
                          <div className="text-xs text-gray-600 mb-1">
                            {formatTimestamp(anomaly.timestamp)}
                          </div>
                          <div className="text-sm text-gray-800 mb-1">
                            {anomaly.description}
                          </div>
                          <div className="text-xs text-gray-500">
                            Location: {anomaly.location[1].toFixed(4)}, {anomaly.location[0].toFixed(4)}
                          </div>
                        </div>
                      ))}
                      {group.anomalies.length > 5 && (
                        <div className="text-xs text-gray-500 text-center py-1">
                          +{group.anomalies.length - 5} more anomalies
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Fixed Footer - Shipment Summary */}
      <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-gray-50">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">Shipment Summary</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">ID:</span>
            <span className="font-medium">{shipment?.id || 'N/A'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Route:</span>
            <span className="font-medium">{shipment?.origin || 'N/A'} → {shipment?.destination || 'N/A'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Distance:</span>
            <span className="font-medium">{shipment?.distance || 'N/A'} km</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Duration:</span>
            <span className="font-medium">{shipment?.duration || 'N/A'} hours</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Status:</span>
            <span className={`font-medium ${
              shipment?.status === 'completed' ? 'text-green-600' :
              shipment?.status === 'in_progress' ? 'text-blue-600' :
              'text-yellow-600'
            }`}>
              {shipment?.status?.replace('_', ' ') || 'N/A'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupedAnomalies; 